/**
 * RequestAccessPage
 *
 * Allows users to join the beta waitlist when they don't have access.
 * Shows a friendly message about the private beta and collects waitlist info.
 */

import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { Check, ArrowLeft, Users, FileText, Clock, Shield } from 'lucide-react';

interface LocationState {
  email?: string;
  message?: string;
}

interface JoinWaitlistResponse {
  success: boolean;
  position?: number;
  message?: string;
}

type CaregiverRole =
  | 'child_iep_504'
  | 'child_medical'
  | 'aging_parent'
  | 'adult_disability'
  | 'other';

const caregiverRoleOptions: { value: CaregiverRole; label: string }[] = [
  { value: 'child_iep_504', label: 'Child with IEP/504 plan' },
  { value: 'child_medical', label: 'Child with medical needs' },
  { value: 'aging_parent', label: 'Aging parent' },
  { value: 'adult_disability', label: 'Adult with disability' },
  { value: 'other', label: 'Other' },
];

export function RequestAccess(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState | null;

  const [email, setEmail] = useState(locationState?.email || '');
  const [name, setName] = useState('');
  const [caregiverRole, setCaregiverRole] = useState<CaregiverRole | ''>('');
  const [reasonForJoining, setReasonForJoining] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(locationState?.message || null);
  const [success, setSuccess] = useState<{ position?: number } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const joinWaitlist = httpsCallable<
        {
          email: string;
          name?: string;
          caregiverRole?: string;
          reasonForJoining?: string;
          source?: string;
        },
        JoinWaitlistResponse
      >(functions, 'joinWaitlistHttp');

      const result = await joinWaitlist({
        email: email.toLowerCase().trim(),
        name: name.trim() || undefined,
        caregiverRole: caregiverRole || undefined,
        reasonForJoining: reasonForJoining.trim() || undefined,
        source: 'web_app',
      });

      if (result.data.success) {
        setSuccess({ position: result.data.position });
      } else {
        setError(result.data.message || 'Failed to join waitlist. Please try again.');
      }
    } catch (err) {
      console.error('Error joining waitlist:', err);
      if (err instanceof Error) {
        // Handle specific error cases
        if (err.message.includes('already')) {
          setError("You're already on the waitlist! We'll notify you when a spot opens up.");
        } else {
          setError(err.message);
        }
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-airy">
        <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-card border border-soft-neutral text-center">
          <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
            <Check className="w-8 h-8 text-green-600" />
          </div>

          <div>
            <h1 className="text-2xl font-display text-deep-forest font-light">
              You're on the list!
            </h1>
            <p className="mt-4 text-muted">
              Thanks for your interest in Hesvara. We'll email you at{' '}
              <span className="font-medium text-ink">{email}</span> when a spot opens up.
            </p>
            {success.position && (
              <p className="mt-4 text-lg">
                Your position: <span className="font-semibold text-evergreen">#{success.position}</span>
              </p>
            )}
          </div>

          <div className="pt-4">
            <button
              onClick={() => navigate('/')}
              className="btn-secondary"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-airy py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Back link */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-sm text-muted hover:text-ink mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </button>

        <div className="bg-white rounded-lg shadow-card border border-soft-neutral overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-evergreen to-deep-forest p-8 text-white">
            <div className="flex justify-center mb-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/20 border border-white/30">
                Private Beta
              </span>
            </div>
            <h1 className="text-3xl font-display font-light text-center">
              Join the Hesvara Waitlist
            </h1>
            <p className="mt-4 text-center text-white/90 max-w-md mx-auto">
              Hesvara is currently in private beta as we refine the experience for caregivers like you.
            </p>
          </div>

          {/* Value Props */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6 bg-soft-neutral/30 border-b border-soft-neutral">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-evergreen/10 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-evergreen" />
              </div>
              <div>
                <h3 className="font-medium text-ink text-sm">Never Miss a Deadline</h3>
                <p className="text-xs text-muted">Track IEP renewals, insurance appeals, and medical appointments</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-evergreen/10 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-evergreen" />
              </div>
              <div>
                <h3 className="font-medium text-ink text-sm">Organize Documents</h3>
                <p className="text-xs text-muted">AI-powered search across all your care documents</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-evergreen/10 flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-evergreen" />
              </div>
              <div>
                <h3 className="font-medium text-ink text-sm">Coordinate Care Teams</h3>
                <p className="text-xs text-muted">Share access with family members and providers</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-evergreen/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-evergreen" />
              </div>
              <div>
                <h3 className="font-medium text-ink text-sm">HIPAA Compliant</h3>
                <p className="text-xs text-muted">Your data is encrypted and protected</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            <div>
              <label htmlFor="email" className="label">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="name" className="label">
                Name <span className="text-muted text-xs font-normal">(optional)</span>
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder="Your name"
              />
            </div>

            <div>
              <label htmlFor="caregiverRole" className="label">
                What best describes your caregiving situation?{' '}
                <span className="text-muted text-xs font-normal">(optional)</span>
              </label>
              <select
                id="caregiverRole"
                value={caregiverRole}
                onChange={(e) => setCaregiverRole(e.target.value as CaregiverRole)}
                className="input"
              >
                <option value="">Select an option...</option>
                {caregiverRoleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="reason" className="label">
                Why do you want to join Hesvara?{' '}
                <span className="text-muted text-xs font-normal">(optional, but helps us prioritize)</span>
              </label>
              <textarea
                id="reason"
                value={reasonForJoining}
                onChange={(e) => setReasonForJoining(e.target.value)}
                className="input min-h-[100px]"
                placeholder="Tell us about your caregiving challenges and what you hope Hesvara can help with..."
              />
            </div>

            {error && (
              <div className="alert alert-error">{error}</div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {isSubmitting && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {isSubmitting ? 'Joining Waitlist...' : 'Join Waitlist'}
            </button>

            <p className="text-xs text-center text-muted">
              Already have access?{' '}
              <button
                type="button"
                onClick={() => navigate('/')}
                className="text-evergreen hover:underline"
              >
                Sign in
              </button>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

export default RequestAccess;
